import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { ApiHelper } from './apiHelper/ApiHelper';
import { loadFaculties } from './facultiesCache';
import PageLoader from './components/PageLoader';

// Section keys mirror the backend Sections enum (spec §0). Values are the
// strings returned by GET /Users/GetSections.
export type Section =
    | "Dashboard"
    | "StudyMaterials"
    | "OnlineLessons"
    | "Practice"
    | "AiTutor"
    | "Community"
    | "Account";

// Rozcestník tiles, in display order (spec §1). Every product-available section
// is shown; `ready: false` ones render as "Připravujeme" and don't navigate yet
// (their pages arrive in F4–F7) instead of pointing at a dead route.
export type SectionTile = {
    section: Section;
    title: string;
    subtitle: string;
    route: string;
    ready: boolean;
};

export const SECTION_TILES: SectionTile[] = [
    { section: "StudyMaterials", title: "Studijní materiály", subtitle: "Texty, videa a dokumenty", route: "/kurz/materialy", ready: true },
    { section: "OnlineLessons", title: "Online lekce", subtitle: "Živě i ze záznamu", route: "/kurz/vyuka", ready: true },
    { section: "Practice", title: "Procvičování", subtitle: "Kvízy a modelové testy", route: "/kurz/procvicovani", ready: true },
    { section: "AiTutor", title: "AI tutor", subtitle: "Vysvětlí, co ti nejde", route: "/kurz/tutor", ready: true },
    { section: "Community", title: "Komunita", subtitle: "Diskuzní kanály a chat", route: "/kurz/komunita", ready: true },
    { section: "Account", title: "Účet", subtitle: "Profil, produkty a platby", route: "/kurz/ucet", ready: true },
];

type SectionsContextValue = {
    sections: Section[];
    loading: boolean;
    hasSection: (section: Section) => boolean;
};

const SectionsContext = createContext<SectionsContextValue>({
    sections: [],
    loading: true,
    hasSection: () => false,
});

export function SectionsProvider({ children }: { children: ReactNode }) {
    const [sections, setSections] = useState<Section[]>([]);
    const [loading, setLoading] = useState(true);

    const api = new ApiHelper();

    async function fetch() {
        try {
            const res = await api.userApi.getSectionsList();
            setSections(await res.json());
        } catch (x: any) {
            api.processErrors(x);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetch();
        // Warm the faculties cache on entry so the onboarding wizard's goal step
        // (and the account/practice pickers) render their options instantly.
        loadFaculties(api).catch(() => {});
    }, []);

    const hasSection = (section: Section) => sections.includes(section);

    return (
        <SectionsContext.Provider value={{ sections, loading, hasSection }}>
            {children}
        </SectionsContext.Provider>
    );
}

export function useSections() {
    return useContext(SectionsContext);
}

// Route guard: renders children only if the section is available in the user's
// product; otherwise redirects to the dashboard (spec §0: hidden, not locked).
export function RequireSection({ section, children }: { section: Section; children: ReactNode }) {
    const { hasSection, loading } = useSections();

    if (loading) return <PageLoader />;
    if (!hasSection(section)) return <Navigate to="/kurz" replace />;

    return <>{children}</>;
}
